// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@bg/App/Components/Notify";
import useGoogleMaps from "@bg/App/Hooks/useGoogleMaps";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { xScreen } from "@bg/XDL/Utilities";
import { styledScrollbar } from "@bg/XDL/Utilities/sharedStyles";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useWarehouseFilterSearch from "@insite/client-framework/Common/Hooks/useWarehouseFilterSearch";
import useWarehouseGoogleMarkers from "@insite/client-framework/Common/Hooks/useWarehouseGoogleMarkers";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { getGeoCodeFromLatLng } from "@insite/client-framework/Common/Utilities/GoogleMaps/getGeoCodeFromAddress";
import withDynamicGoogleMaps, { HasDynamicGoogleMaps } from "@insite/client-framework/Common/withDynamicGoogleMaps";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadWarehouses from "@insite/client-framework/Store/Components/FindLocationModal/Handlers/LoadWarehouses";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWarehousesDataView } from "@insite/client-framework/Store/Data/Warehouses/WarehousesSelectors";
import translate from "@insite/client-framework/Translate";
import { PaginationModel, WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoCondensedDisplay, {
    AddressInfoCondensedDisplayStyles,
} from "@insite/content-library/Components/AddressInfoCondensedDisplay";
import DistanceDisplay, { DistanceUnitOfMeasure } from "@insite/content-library/Components/DistanceDisplay";
import GoogleMapsDirectionLink from "@insite/content-library/Components/GoogleMapsDirectionLink";
import WarehouseFindLocationPagination from "@insite/content-library/Components/WarehouseFindLocationPagination";
import WarehouseGoogleMap, { WarehouseGoogleMapStyles } from "@insite/content-library/Components/WarehouseGoogleMap";
import WarehouseHoursLink from "@insite/content-library/Components/WarehouseHoursLink";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { FormFieldClickable } from "@insite/mobius/FormField";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    modalIsOpen: boolean;
    onWarehouseSelected: (warehouse: WarehouseModel) => void;
    onModalClose: () => void;
    forCart?: boolean;
    extendedStyles?: FindLocationModalStyles;
    useLoadScript: ({ googleMapsApiKey }: { googleMapsApiKey: any }) => { isLoaded: boolean };
}

const mapStateToProps = (state: ApplicationState, { forCart }: OwnProps) => {
    const { cartId } = getQueryStrings(state);
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const pickUpWarehouse = forCart ? cart?.defaultWarehouse : getSession(state).pickUpWarehouse;

    return {
        warehousesDataView: getWarehousesDataView(state, state.components.findLocationModal.getWarehousesParameter),
        pickUpWarehouse,
        settings: getSettingsCollection(state),
    };
};

const mapDispatchToProps = {
    loadWarehouses,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasDynamicGoogleMaps;

export interface FindLocationModalStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    modal?: ModalPresentationProps;
    modalContent?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    form?: InjectableCss;
    searchGridContainer?: GridContainerProps;
    searchTextFieldGridItem?: GridItemProps;
    searchTextField?: TextFieldPresentationProps;
    searchRadiusGridItem?: GridItemProps;
    searchRadiusText?: TextFieldPresentationProps;
    searchButtonGridItem?: GridItemProps;
    searchButton?: ButtonPresentationProps;
    searchResultsTextGridItem?: GridItemProps;
    searchResultsText?: TypographyPresentationProps;
    distanceUnitOfMeasureGridItem?: GridItemProps;
    distanceUnitOfMeasureRadioGroup?: RadioGroupProps;
    distanceUnitOfMeasureImperialRadio?: RadioComponentProps;
    distanceUnitOfMeasureMetricRadio?: RadioComponentProps;
    siteMessageGridItem?: GridItemProps;
    siteMessageGeocodeErrorText?: TypographyPresentationProps;
    siteMessageResultsErrorText?: TypographyPresentationProps;
    searchResultGridItem?: GridItemProps;
    searchResultGridContainer?: GridContainerProps;
    searchResultLeftGridItem?: GridItemProps;
    searchResultWarehouseDetailsGridContainer?: GridContainerProps;
    searchResultDisplayNumberGridItem?: GridItemProps;
    warehouseDisplayNumberText?: TypographyPresentationProps;
    searchResultWarehouseGridItem?: GridItemProps;
    warehouseNameText?: TypographyPresentationProps;
    searchResultAddressInfoOffsetGridItem?: GridItemProps;
    searchResultAddressInfoIcon?: IconPresentationProps;
    searchResultAddressInfoGridItem?: GridItemProps;
    warehouseAddressInfoCondensed?: AddressInfoCondensedDisplayStyles;
    searchResultLinksAndDistanceContainer?: GridContainerProps;
    searchResultPhoneOffsetGridItem?: GridItemProps;
    searchResultPhoneIcon?: IconPresentationProps;
    searchResultPhoneGridItem?: GridItemProps;
    searchResultPhoneLink?: LinkPresentationProps;
    searchResultSelectGridItem?: GridItemProps;
    searchResultSelectButton?: ButtonPresentationProps;
    searchResultLinksOffsetGridItem?: GridItemProps;
    searchResultHoursGridItem?: GridItemProps;
    warehouseHoursLink?: LinkPresentationProps;
    searchResultGoogleMapsDirectionLinkGridItem?: GridItemProps;
    warehouseGoogleMapsDirectionLink?: LinkPresentationProps;
    searchResultDistanceGridItem?: GridItemProps;
    warehouseDistanceDisplayText?: TypographyPresentationProps;
    searchResultDividerGridItem?: GridItemProps;
    searchResultDivider?: InjectableCss;
    paginationGridItem?: GridItemProps;
    pagination?: PaginationPresentationProps;

    rightColumnGridItem?: GridItemProps;
    rightColumnSelectedLocationGridItem?: GridItemProps;
    currentlySelectedGridContainer?: GridContainerProps;
    currentlySelectedHeaderGridItem?: GridItemProps;
    currentlySelectedHeaderText?: TypographyPresentationProps;
    currentlySelectedDetailsGridItem?: GridItemProps;
    selectedWarehouseGridContainer?: GridContainerProps;
    searchInputGridContainer?: GridContainerProps;
    selectedWarehouseDetailsGridItem?: GridItemProps;
    selectedWarehouseNameText?: TypographyPresentationProps;
    selectedWarehouseAddressDisplay?: AddressInfoCondensedDisplayStyles;
    selectedWarehousePhoneLink?: LinkPresentationProps;
    selectedWarehouseLinksGridItem?: GridItemProps;
    selectedWarehouseLink?: LinkPresentationProps;
    selectedWarehouseGoogleMapsDirectionLink?: LinkPresentationProps;
    selectedWarehouseDistanceText?: TypographyPresentationProps;
    warehouseGoogleMapGridItem?: GridItemProps;
    warehouseGoogleMap?: WarehouseGoogleMapStyles;
    warehouseHoursModal?: ModalPresentationProps;
    hoursContainer?: InjectableCss;
    searchResultWarehouseRightSectionWrapper?: InjectableCss;
    searchResultAddressInfoContainer?: GridContainerProps;
    searchIcon?: IconPresentationProps;
}

const getCommonLinkStyles = (icon?: React.NamedExoticComponent | string) => {
    return {
        color: "secondary",
        typographyProps: { weight: fontConfig.weight.regular },
        icon: { iconProps: { src: icon, size: 10 } },
    } as LinkPresentationProps;
};
const withBorderTop = css`
    border-top: 1px solid ${getColor("common.border")};
`;

export const findLocationModalStyles: FindLocationModalStyles = {
    centeringWrapper: {
        css: css`
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    modal: {
        sizeVariant: "large",
        cssOverrides: {
            ...getProp("theme.modal.defaultProps.cssOverrides"),
        },
    },
    modalContent: {
        gap: 10,
    },
    leftColumnGridItem: {
        width: [12, 12, 12, 6, 5],
        css: css`
            padding-right: 18px;
            ${xScreen("lte", "sm")} {
                padding-right: 0 !important;
            }
        `,
    },
    form: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    searchRadiusText: {
        css: css`
            width: 100%;
            display: block;
        `,
    },
    searchGridContainer: {
        gap: 0,
        css: css`
            > div {
                width: 100%;
            }
        `,
    },
    searchTextFieldGridItem: {
        width: [12, 11, 11, 11, 11],
        css: css`
            padding-bottom: 5px;
        `,
    },
    searchTextField: {
        cssOverrides: {
            formInputWrapper: css`
                ${getProp("theme.formField.defaultProps.cssOverrides.formInputWrapper")}
                ${FormFieldClickable as any} {
                    background-color: ${getColor("primary")};
                    display: flex;
                    justify-content: center;
                    > span {
                        color: ${getColor("common.white")};
                        right: unset;
                        top: unset;
                        position: unset;
                        padding: 0;
                        > svg {
                            fill: ${getColor("common.white")};
                            width: 24px;
                            height: 24px;
                        }
                    }
                    &:hover {
                        background-color: ${getColor("secondary")};
                    }
                }

                input {
                    padding-right: 45px !important;
                }
            `,
        },
    },
    searchRadiusGridItem: {
        width: [12, 11, 11, 11, 11],
        css: css`
            padding-top: 10px;
        `,
    },
    searchButtonGridItem: {
        width: [12, 1, 1, 1, 1],
        css: css`
            padding-top: 10px;
            button {
                margin-top: 19px;
                padding-left: 15px;
                padding-right: 15px;
            }

            ${xScreen("lte", "sm")} {
                button {
                    width: 100% !important;
                }
            }

            display: inline-block;
            width: 100%;
            flex-direction: row;
            text-align: right;
            align-self: flex-end;
            & > div {
                display: inline-flex;
            }
        `,
    },
    searchResultsTextGridItem: {
        width: 12,
        align: "middle",
        css: css`
            align-items: center;
            justify-content: space-between;
            padding: 10px;
            background: ${getColor("common.background")};
            border-bottom: none;
            width: 100% !important;
        `,
    },
    searchResultsText: { weight: fontConfig.weight.bold },
    distanceUnitOfMeasureGridItem: {
        width: 8,
        align: "middle",
    },
    distanceUnitOfMeasureRadioGroup: {
        css: css`
            display: inline-block;
            flex-direction: row;
            text-align: right;
            & > div {
                display: inline-flex;
                margin: 0px 10px;
            }
        `,
    },
    siteMessageGridItem: {
        width: 12,
    },
    siteMessageGeocodeErrorText: {
        color: "danger",
        weight: fontConfig.weight.bold,
    },
    siteMessageResultsErrorText: {
        color: "danger",
        weight: fontConfig.weight.bold,
    },
    searchResultGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            height: 400px;
            overflow-y: auto;
            overscroll-behavior: contain;
            padding-bottom: 25px;
            ${styledScrollbar()};
        `,
    },
    searchResultGridContainer: {
        gap: 10,
    },
    searchResultLeftGridItem: {
        width: 9,
    },
    searchResultWarehouseDetailsGridContainer: {
        gap: 0,
        css: [
            GridContainer.defaultProps?.css,
            css`
                flex-grow: initial;
                padding: 10px 10px 0 10px;
                ${(props: any) => (props.withBorderTop ? withBorderTop : "")}
            `,
        ],
    },
    searchResultDisplayNumberGridItem: {
        width: 2,
        css: css`
            justify-content: center;
        `,
    },
    warehouseDisplayNumberText: {
        color: "common.ui1",
        weight: fontConfig.weight.bold,
    },
    searchResultWarehouseGridItem: {
        width: 12,
        css: css`
            justify-content: space-between;
            @media (max-width: 481px) {
                flex-direction: column;
            }
        `,
    },
    warehouseAddressInfoCondensed: {
        wrapper: {
            css: css`
                margin-top: 10px;
                font-style: normal;
            `,
        },
    },

    warehouseNameText: {
        color: "primary",
        weight: fontConfig.weight.bold,
    },
    searchResultAddressInfoOffsetGridItem: {
        width: 2,
        css: css`
            justify-content: center;
        `,
    },
    searchResultAddressInfoIcon: {
        color: "primary",
    },
    searchResultAddressInfoGridItem: {
        width: 12,
        css: css`
            display: block;
            border-bottom: 1px solid ${getColor("common.border")};
            padding-bottom: 10px;
            &.last {
                border-bottom: none;
            }
        `,
    },
    searchResultPhoneOffsetGridItem: {
        width: 2,
        css: css`
            justify-content: center;
        `,
    },
    searchResultPhoneIcon: {
        size: 14,
    },
    searchResultPhoneGridItem: {
        width: 12,
    },
    searchResultPhoneLink: {
        ...getCommonLinkStyles("Phone"),
        css: css`
            ${getProp("theme.link.defaultProps.css")};
            margin-top: 5px;
        `,
    },
    warehouseGoogleMapsDirectionLink: { ...getCommonLinkStyles("MapPin"), color: "primary" },
    searchResultSelectGridItem: {
        width: 3,
    },
    searchResultSelectButton: {},
    searchResultLinksAndDistanceContainer: {
        gap: 0,
        css: css`
            width: 100%;
            padding: 0;
            margin: 0;
        `,
    },
    searchResultLinksOffsetGridItem: {
        width: 2,
    },
    searchResultHoursGridItem: {
        width: 3,
    },
    warehouseHoursLink: getCommonLinkStyles(),
    searchResultGoogleMapsDirectionLinkGridItem: {
        width: 8,
        css: css`
            justify-content: flex-end;
        `,
    },
    searchResultDistanceGridItem: {
        width: 4,
        css: css`
            justify-content: flex-start;
        `,
    },
    searchResultDividerGridItem: {
        width: 12,
    },
    searchResultDivider: {
        css: css`
            margin: 24px 0;
            border: 1px solid rgba(74, 74, 74, 0.2);
            width: 100%;
        `,
    },
    paginationGridItem: {
        width: 12,
        css: css`
            justify-content: center;
            border-top: none;
            padding: 10px;
            nav {
                justify-content: center;
            }
        `,
    },
    pagination: {
        cssOverrides: {
            perPageSelect: css`
                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                display: none;
                            `,
                            null,
                            null,
                            null,
                            null,
                        ],
                        "min",
                    )}
            `,
        },
    },
    rightColumnGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    rightColumnSelectedLocationGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            align-items: flex-end;
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            order: -1;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    currentlySelectedGridContainer: { gap: 5 },
    currentlySelectedHeaderGridItem: {
        width: 12,
    },
    currentlySelectedHeaderText: {
        weight: fontConfig.weight.bold,
    },
    currentlySelectedDetailsGridItem: {
        width: 12,
    },
    selectedWarehouseGridContainer: {
        gap: 0,
    },
    selectedWarehouseDetailsGridItem: {
        width: [12, 8, 8, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    selectedWarehouseNameText: {
        color: "primary",
        weight: fontConfig.weight.bold,
    },
    selectedWarehouseLinksGridItem: {
        width: [12, 4, 4, 6, 6],
        css: css`
            flex-direction: column;
            align-items: flex-end;
            margin-left: 5px;
        `,
    },
    selectedWarehouseDistanceText: {
        css: css`
            margin-top: auto;
        `,
    },
    warehouseGoogleMapGridItem: {
        width: 12,
    },
    warehouseHoursModal: {
        sizeVariant: "small",
    },
    warehouseGoogleMap: {
        infoWindowWarehouseHoursLink: getCommonLinkStyles(),
        infoWindowGoogleMapsDirectionLink: { ...getCommonLinkStyles("MapPin"), color: "primary" },
    },
    hoursContainer: {
        css: css`
            white-space: pre-wrap;
        `,
    },
    searchResultWarehouseRightSectionWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            flex-shrink: 0;
            margin-left: 5px;
            @media (max-width: 481px) {
                width: 100%;
                margin-left: 0;
            }
        `,
    },
    searchResultAddressInfoContainer: {
        gap: 0,
        css: css`
            margin-top: 5px;
            > div {
                align-items: center;
            }
        `,
    },
    searchIcon: {
        src: "Search",
        size: 16,
        color: "common.white",
    },
    searchInputGridContainer: {
        gap: 10,
    },
};

const StyledForm = getStyledWrapper("form");
const StyledHr = getStyledWrapper("hr");

let lastFocusableElement: any = null;

const FindLocationModal = ({
    pickUpWarehouse,
    settings,
    warehousesDataView,
    modalIsOpen,
    onWarehouseSelected,
    onModalClose,
    extendedStyles,
    loadWarehouses,
    useLoadScript,
}: Props) => {
    const [styles] = useState(() => mergeToNew(findLocationModalStyles, extendedStyles));
    const [distanceUnitOfMeasure, setDistanceUnitOfMeasure] = useState<DistanceUnitOfMeasure>("Metric");
    const [warehouseHoursToDisplay, setWarehouseHoursToDisplay] = useState<WarehouseModel | undefined>(undefined);
    const [warehouseHoursToDisplayModalOpen, setWarehouseHoursToDisplayModalOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const googleMapsApiKey = (settings.customSettings as any).storefrontGoogleMapsAPIKey;
    const { isLoaded } = useLoadScript({ googleMapsApiKey });
    const {
        googleMap,
        setGoogleMap,
        currentLocation,
        setCurrentLocation,
        isGoogleMapsScriptsLoaded,
        setBounds,
        setMapCenter,
        locationKnown,
        setLocationKnown,
    } = useGoogleMaps({
        isLoaded,
        isShown: modalIsOpen,
    });

    // Manage Selected Warehouse
    const [selectedWarehouse, setSelectedWarehouse] = useState<WarehouseModel | undefined>(undefined);
    const [showSelectedWarehouse, setShowSelectedWarehouse] = useState<boolean>(false);
    const [defaultRadius, setDefaultRadius] = useState<number>(0);
    useEffect(() => {
        setSelectedWarehouse(pickUpWarehouse || undefined);
    }, [pickUpWarehouse]);

    // Manage Local Warehouses
    const [warehouses, setWarehouses] = useState<WarehouseModel[]>([]);
    const [resultCount, setResultCount] = useState<number>(0);
    const [warehousesPagination, setWarehousesPagination] = useState<PaginationModel | undefined>();

    // Manage Loading Warehouses on State Changes
    const {
        doSearch,
        warehouseSearchFilter,
        setWarehouseSearchFilter,
        radiusSearchFilter,
        setRadiusSearchFilter,
        setPage,
        setPageSize,
    } = useWarehouseFilterSearch({
        loadWarehouses,
        currentLocation,
        setCurrentLocation,
        setLocationKnown,
        defaultRadius,
        selectedWarehouse,
        setSelectedWarehouse,
        setShowSelectedWarehouse,
    });

    // Manage Bounds of Map
    useEffect(() => {
        const bounds = [];
        closeInfoWindows();
        warehouses.forEach(warehouse => {
            bounds.push({ lat: warehouse.latitude, lng: warehouse.longitude });
        });
        if (showSelectedWarehouse && selectedWarehouse) {
            bounds.push({ lat: selectedWarehouse.latitude, lng: selectedWarehouse.longitude });
        }
        setBounds(bounds);
    }, [googleMap, warehouses, selectedWarehouse, showSelectedWarehouse, currentLocation]);

    // Manage Map Markers
    const { mapMarkersElements, currentLocationInfoWindow, warehouseInfoWindow, closeInfoWindows, getWarehouseNumber } =
        useWarehouseGoogleMarkers({
            warehouses,
            warehouseSearchFilter,
            warehousesPagination,
            currentLocation,
            selectedWarehouse,
            showSelectedWarehouse,
        });

    useEffect(() => {
        if (!isGoogleMapsScriptsLoaded) {
            return;
        }
        if (warehousesDataView.value && !warehousesDataView.isLoading) {
            const {
                pagination,
                value: warehouses,
                defaultRadius,
                defaultLatitude,
                defaultLongitude,
            } = warehousesDataView;
            // Find default location
            getGeoCodeFromLatLng(defaultLatitude, defaultLongitude)
                .then(result => {
                    const isSearchTextAlreadyFormatted = !!result.find(
                        o => o.formatted_address === warehouseSearchFilter,
                    );
                    if (isSearchTextAlreadyFormatted) {
                        return;
                    }

                    setWarehouseSearchFilter(result[0].formatted_address);
                })
                .finally(() => {
                    setWarehousesPagination(pagination || undefined);
                    setResultCount(pagination?.totalItemCount || 0);
                    setDefaultRadius(defaultRadius);
                    setRadiusSearchFilter(defaultRadius);
                    setMapCenter(new google.maps.LatLng(defaultLatitude, defaultLongitude));
                    setWarehouses(warehouses || []);
                    setIsLoading(false);
                });
        } else {
            setIsLoading(true);
        }
    }, [warehousesDataView, isGoogleMapsScriptsLoaded]);

    // Manage modal focus when tabbing through the map insert
    const keysPressed = {
        shift: false,
        tab: false,
    };

    let focusableElementInterval: any = null;

    const updateLastFocusableElement = () => {
        const focusableElements = document.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusableElements.length > 0) {
            lastFocusableElement = focusableElements[focusableElements.length - 1];
        }
    };

    const handleKeydownTabInModal = (event: KeyboardEvent) => {
        // collect what keys are being pressed in the case that the user is trying to shift+tab while on the close modal button
        if (event.key === "Tab") {
            keysPressed.tab = true;
        } else if (event.key === "Shift") {
            keysPressed.shift = true;
        }
        // if straight tab and on the final focusable element (the terms of use html page link) refocus onto the close modal button
        if (event.key === "Tab" && !keysPressed.shift) {
            const currentElement = document.activeElement;
            // using the .html here as it is the only link on the page to an html page. Cannot rely on checking "terms of use" is the inner HTML since that will not always be in English

            if (currentElement === lastFocusableElement) {
                event.preventDefault();
                document.getElementById("CloseModalButton")?.focus();
            }
        } else if ((event.key === "Tab" && keysPressed.shift) || (event.key === "Shift" && keysPressed.tab)) {
            // if pressing shift+tab and the focus is on the close modal button, send the focus to the terms of use element which is the last anchor tag on the google map
            if (document.activeElement?.id === "CloseModalButton" && lastFocusableElement) {
                event.preventDefault();
                lastFocusableElement.focus();
            }
        }
    };

    const handleKeyupTabInModal = (event: KeyboardEvent) => {
        if (event.key === "Tab") {
            keysPressed.tab = false;
        } else if (event.key === "Shift") {
            keysPressed.shift = false;
        }
    };

    useEffect(() => {
        if (modalIsOpen && !warehouseHoursToDisplayModalOpen) {
            window.addEventListener("keydown", handleKeydownTabInModal);
            window.addEventListener("keyup", handleKeyupTabInModal);
            focusableElementInterval = setInterval(updateLastFocusableElement, 1000);
        }
        return () => {
            window.removeEventListener("keydown", handleKeydownTabInModal);
            window.removeEventListener("keyup", handleKeyupTabInModal);
            clearInterval(focusableElementInterval);
        };
    }, [modalIsOpen, warehouseHoursToDisplayModalOpen]);

    const modalCloseHandler = () => {
        onModalClose();
    };

    const handleDistanceUnitOfMeasureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as DistanceUnitOfMeasure;
        setDistanceUnitOfMeasure(value);
    };

    const handleOpenHoursModal = (warehouse: WarehouseModel) => {
        setWarehouseHoursToDisplayModalOpen(!!warehouse.hours);
        setWarehouseHoursToDisplay(warehouse);
    };

    const handleHoursModalClose = () => {
        setWarehouseHoursToDisplayModalOpen(false);
        setWarehouseHoursToDisplay(undefined);
    };

    const generateOpenWarehouseHoursFor = (warehouse: WarehouseModel) => {
        return () => handleOpenHoursModal(warehouse);
    };

    const generateWarehouseSelectClickHandler = (warehouse: WarehouseModel) => {
        return () => onWarehouseSelected(warehouse);
    };

    const isNotFinalResult = (resultIndex: number) => {
        if (!warehousesPagination) {
            return false;
        }
        return warehousesPagination.pageSize > resultIndex + 1;
    };
    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (!isLoading) {
            doSearch(warehouseSearchFilter);
        }
    };
    const handleSearchRadiusChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRadiusSearchFilter(Number(event.target.value));
    };

    return (
        <Modal
            {...styles.modal}
            headline={translate("Select Will Call Location")}
            isOpen={modalIsOpen}
            handleClose={modalCloseHandler}
        >
            <GridContainer {...styles.modalContent} data-test-selector="findLocationModal">
                <GridItem {...styles.leftColumnGridItem}>
                    <StyledForm {...styles.form} onSubmit={handleSubmit}>
                        <GridContainer {...styles.searchInputGridContainer}>
                            <GridItem {...styles.searchTextFieldGridItem}>
                                <SearchLocationsTextField
                                    styles={styles.searchTextField}
                                    setWarehouseSearchFilter={setWarehouseSearchFilter}
                                    warehouseSearchFilter={warehouseSearchFilter}
                                />
                            </GridItem>
                            <GridItem {...styles.searchRadiusGridItem}>
                                <TextField
                                    {...styles.searchRadiusText}
                                    label={translate("Search Radius")}
                                    value={radiusSearchFilter}
                                    onChange={handleSearchRadiusChanged}
                                    type="number"
                                    min={0}
                                    placeholder={translate("Search Radius")}
                                />
                            </GridItem>
                            <GridItem {...styles.searchButtonGridItem}>
                                <Button
                                    {...styles.searchButton}
                                    type="submit"
                                    data-test-selector="findLocationModal_searchButton"
                                >
                                    <Icon {...styles.searchIcon} />
                                </Button>
                            </GridItem>
                        </GridContainer>
                    </StyledForm>
                </GridItem>

                <GridItem {...styles.rightColumnSelectedLocationGridItem}>
                    {selectedWarehouse && (
                        <GridContainer {...styles.selectedWarehouseGridContainer}>
                            <GridItem {...styles.selectedWarehouseDetailsGridItem}>
                                <Typography
                                    {...styles.selectedWarehouseNameText}
                                    data-test-selector="pickupLocationModal_selectedWarehouseName"
                                >
                                    {selectedWarehouse.description || selectedWarehouse.name}
                                    <Typography color="secondary" weight="normal">
                                        {translate(" (Selected)")}
                                    </Typography>
                                </Typography>
                                <AddressInfoCondensedDisplay
                                    {...selectedWarehouse}
                                    extendedStyles={styles.selectedWarehouseAddressDisplay}
                                />
                                {selectedWarehouse.phone && (
                                    <>
                                        <Link
                                            href={`tel:${selectedWarehouse.phone}`}
                                            {...styles.searchResultPhoneLink}
                                            onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                                                e.defaultPrevented = true; // fix OOTB issue, the telephone/email links not opening
                                            }}
                                            aria-label={translate(
                                                "Selected Warehouse Phone: {0}",
                                                selectedWarehouse.phone,
                                            )}
                                        >
                                            {selectedWarehouse.phone}
                                        </Link>
                                    </>
                                )}
                            </GridItem>
                            <GridItem {...styles.selectedWarehouseLinksGridItem}>
                                <StyledWrapper
                                    css={css`
                                        display: flex;
                                        flex-direction: column;
                                        align-items: flex-end;
                                        height: 100%;
                                    `}
                                >
                                    {selectedWarehouse.hours && (
                                        <WarehouseHoursLink
                                            {...styles.warehouseHoursLink}
                                            warehouse={selectedWarehouse}
                                            onOpenWarehouseHours={handleOpenHoursModal}
                                        />
                                    )}
                                    <GoogleMapsDirectionLink
                                        {...styles.warehouseGoogleMapsDirectionLink}
                                        {...selectedWarehouse}
                                    />
                                    {selectedWarehouse.distance > 0.01 && (
                                        <DistanceDisplay
                                            {...styles.selectedWarehouseDistanceText}
                                            distance={selectedWarehouse.distance}
                                            unitOfMeasure={distanceUnitOfMeasure}
                                        />
                                    )}
                                </StyledWrapper>
                            </GridItem>
                        </GridContainer>
                    )}
                </GridItem>
                <GridItem {...styles.leftColumnGridItem}>
                    <GridContainer {...styles.searchGridContainer}>
                        <GridItem {...styles.searchResultsTextGridItem}>
                            <Typography {...styles.searchResultsText}>
                                {translate("{0} Results").replace("{0}", `${resultCount}`)}
                            </Typography>
                            <RadioGroup
                                {...styles.distanceUnitOfMeasureRadioGroup}
                                value={distanceUnitOfMeasure}
                                onChangeHandler={handleDistanceUnitOfMeasureChange}
                                className="inline-group"
                            >
                                <Radio {...styles.distanceUnitOfMeasureImperialRadio} value="Imperial">
                                    {translate("mi")}
                                </Radio>
                                <Radio {...styles.distanceUnitOfMeasureMetricRadio} value="Metric">
                                    {translate("km")}
                                </Radio>
                            </RadioGroup>
                        </GridItem>

                        <GridItem {...styles.siteMessageGridItem}>
                            {!isLoading && !locationKnown && warehouses.length === 0 && (
                                <Notify messageType="danger" body={siteMessage("PickUpLocation_GeocodeErrorMessage")} />
                            )}
                            {!isLoading && locationKnown && warehouses.length === 0 && (
                                <Notify
                                    messageType="danger"
                                    data-test-selector="findLocationModal_noresult"
                                    body={
                                        showSelectedWarehouse
                                            ? siteMessage("PickUpLocation_SelectedLocationIsOnlyResult")
                                            : siteMessage("PickUpLocation_NoResultsMessage")
                                    }
                                />
                            )}
                        </GridItem>

                        {(!warehousesDataView || !warehousesDataView.value) && isLoading && (
                            <GridItem {...styles.searchResultGridItem}>
                                <StyledWrapper {...styles.centeringWrapper}>
                                    <LoadingSpinner {...styles.spinner} />
                                </StyledWrapper>
                            </GridItem>
                        )}
                        {warehousesDataView && warehousesDataView.value && (
                            <GridItem {...styles.searchResultGridItem}>
                                {warehouses.map((warehouse, index) => (
                                    <GridContainer
                                        data-test-selector="findLocationModal_warehouse"
                                        data-test-key={`${warehouse.id}`}
                                        key={warehouse.id}
                                        {...{ withBorderTop: index === 0 }}
                                        {...styles.searchResultWarehouseDetailsGridContainer}
                                    >
                                        <GridItem
                                            {...styles.searchResultWarehouseGridItem}
                                            data-test-selector={`pickUpLocationModal_warehouseName_${index}`}
                                        >
                                            <StyledWrapper>
                                                <Typography {...styles.warehouseDisplayNumberText}>
                                                    {`${getWarehouseNumber(index)}. `}
                                                </Typography>
                                                <Typography {...styles.warehouseNameText}>
                                                    {warehouse.description || warehouse.name}
                                                </Typography>
                                                <AddressInfoCondensedDisplay
                                                    extendedStyles={styles.warehouseAddressInfoCondensed}
                                                    {...warehouse}
                                                />
                                                {warehouse.phone && (
                                                    <Link
                                                        href={`tel:${warehouse.phone}`}
                                                        {...styles.searchResultPhoneLink}
                                                        onClick={(
                                                            e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
                                                        ) => {
                                                            e.defaultPrevented = true; // fix OOTB issue, the telephone/email links not opening
                                                        }}
                                                        aria-label={translate("Warehouse Phone: {0}", warehouse.phone)}
                                                    >
                                                        {warehouse.phone}
                                                    </Link>
                                                )}
                                            </StyledWrapper>
                                            <StyledWrapper {...styles.searchResultWarehouseRightSectionWrapper}>
                                                {warehouse.hours && (
                                                    <WarehouseHoursLink
                                                        {...styles.warehouseHoursLink}
                                                        warehouse={warehouse}
                                                        onOpenWarehouseHours={generateOpenWarehouseHoursFor(warehouse)}
                                                        data-test-selector={`pickUpLocationModal_warehouseHours_${index}`}
                                                    />
                                                )}
                                                <GoogleMapsDirectionLink
                                                    {...styles.warehouseGoogleMapsDirectionLink}
                                                    {...warehouse}
                                                />
                                            </StyledWrapper>
                                        </GridItem>
                                        <GridItem
                                            {...styles.searchResultAddressInfoGridItem}
                                            className={index === warehouses.length - 1 ? "last" : ""}
                                        >
                                            <GridContainer {...styles.searchResultAddressInfoContainer}>
                                                <GridItem {...styles.searchResultDistanceGridItem}>
                                                    <DistanceDisplay
                                                        {...styles.warehouseDistanceDisplayText}
                                                        distance={warehouse.distance}
                                                        unitOfMeasure={distanceUnitOfMeasure}
                                                        data-test-selector={`pickUpLocationModal_warehouseDistance_${index}`}
                                                    />
                                                </GridItem>
                                                <GridItem {...styles.searchResultGoogleMapsDirectionLinkGridItem}>
                                                    <Button
                                                        {...styles.searchResultSelectButton}
                                                        type="button"
                                                        onClick={generateWarehouseSelectClickHandler(warehouse)}
                                                        data-test-selector="findLocationModal_warehouseSelect"
                                                    >
                                                        {translate("Select")}
                                                    </Button>
                                                </GridItem>
                                            </GridContainer>
                                        </GridItem>
                                    </GridContainer>
                                ))}
                            </GridItem>
                        )}

                        <GridItem {...styles.paginationGridItem}>
                            <WarehouseFindLocationPagination
                                {...styles.pagination}
                                warehouses={warehouses}
                                warehousesPagination={warehousesPagination}
                                setPage={setPage}
                                setPageSize={setPageSize}
                            />
                        </GridItem>
                    </GridContainer>
                </GridItem>

                <GridItem {...styles.rightColumnGridItem}>
                    <GridContainer {...styles.currentlySelectedGridContainer}>
                        <GridItem {...styles.warehouseGoogleMapGridItem}>
                            <WarehouseGoogleMap
                                extendedStyles={styles.warehouseGoogleMap}
                                currentLocation={currentLocation}
                                warehouseSearchFilter={warehouseSearchFilter}
                                distanceUnitOfMeasure={distanceUnitOfMeasure}
                                setGoogleMap={setGoogleMap}
                                mapMarkerElements={mapMarkersElements}
                                warehouseInfoWindow={warehouseInfoWindow}
                                currentLocationInfoWindow={currentLocationInfoWindow}
                                handleOpenWarehouseHours={handleOpenHoursModal}
                            />
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
            <Modal
                {...styles.warehouseHoursModal}
                headline={translate("Hours")}
                isOpen={warehouseHoursToDisplayModalOpen}
                handleClose={handleHoursModalClose}
            >
                <StyledWrapper {...styles.hoursContainer}>
                    {parse(warehouseHoursToDisplay?.hours || "", parserOptions)}
                </StyledWrapper>
            </Modal>
        </Modal>
    );
};

interface SearchLocationsTextFieldProps {
    styles?: TextFieldPresentationProps;
    warehouseSearchFilter: string;
    setWarehouseSearchFilter: (filter: string) => void;
}

const SearchLocationsTextField = ({
    styles,
    setWarehouseSearchFilter,
    warehouseSearchFilter,
}: SearchLocationsTextFieldProps) => {
    const handleSearchLocationsChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setWarehouseSearchFilter(event.target.value);
    };

    return (
        <TextField
            {...styles}
            label={translate("Search Locations")}
            value={warehouseSearchFilter}
            onChange={handleSearchLocationsChanged}
            placeholder={translate("Search by location name, city, state, or zip")}
            data-test-selector="findLocationModal_locationSearch"
        />
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withDynamicGoogleMaps(FindLocationModal));
